/**
 * Arrival Notification Service
 * 
 * Handles ETA sharing and arrival notifications to dispatch/team.
 * Can send notifications via:
 * - In-app notifications (Firebase)
 * - SMS (via Cloud Functions)
 * - Push notifications
 */

import { db } from '../firebase/client';
import {
    collection,
    addDoc,
    Timestamp
} from 'firebase/firestore';
import { toast } from 'sonner';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface ArrivalNotification {
    id?: string;
    type: 'eta_update' | 'arrival' | 'departure' | 'delay';
    wellId: string;
    wellName: string;
    userId: string;
    userName: string;
    etaMinutes?: number;
    etaTime?: Date;
    actualArrivalTime?: Date;
    message?: string;
    notifyDispatch: boolean;
    notifyVia: ('app' | 'sms' | 'push')[];
    status: 'pending' | 'sent' | 'failed';
    createdAt: Date;
    companyId: string;
}

export interface ETASharingPreferences {
    autoShareETA: boolean;
    notifyOnArrival: boolean;
    notifyOnDeparture: boolean;
    notifyOnDelay: boolean;
    delayThresholdMinutes: number;
    recipients: Array<{
        userId: string;
        userName: string;
        phone?: string;
        email?: string;
        role: 'dispatch' | 'supervisor' | 'team';
    }>;
}

// ============================================================================
// SERVICE CLASS
// ============================================================================

class ArrivalNotificationService {
    private companyId: string | null = null;
    private userId: string | null = null;
    private userName: string | null = null;
    private preferences: ETASharingPreferences | null = null;

    /**
     * Initialize the service
     */
    initialize(
        companyId: string,
        userId: string,
        userName: string,
        preferences?: Partial<ETASharingPreferences>
    ): void {
        this.companyId = companyId;
        this.userId = userId;
        this.userName = userName;
        this.preferences = {
            autoShareETA: false,
            notifyOnArrival: true,
            notifyOnDeparture: false,
            notifyOnDelay: true,
            delayThresholdMinutes: 15,
            recipients: [],
            ...preferences,
        };
    }

    /**
     * Send ETA update notification
     */
    async sendETAUpdate(
        wellId: string,
        wellName: string,
        etaMinutes: number,
        options: {
            notifyDispatch?: boolean;
            customMessage?: string;
        } = {}
    ): Promise<string | null> {
        if (!this.canSendNotifications()) {
            return null;
        }

        const etaTime = new Date(Date.now() + etaMinutes * 60 * 1000);

        const notification: Omit<ArrivalNotification, 'id'> = {
            type: 'eta_update',
            wellId,
            wellName,
            userId: this.userId!,
            userName: this.userName!,
            etaMinutes,
            etaTime,
            notifyDispatch: options.notifyDispatch ?? true,
            notifyVia: ['app'],
            status: 'pending',
            message: options.customMessage || `ETA ${etaMinutes} min to ${wellName}`,
            createdAt: new Date(),
            companyId: this.companyId!,
        };

        try {
            const id = await this.saveNotification(notification);
            return id;
        } catch (error) {
            console.error('Error sending ETA update:', error);
            return null;
        }
    }

    /**
     * Send arrival notification
     */
    async sendArrivalNotification(
        wellId: string,
        wellName: string,
        options: {
            notifyDispatch?: boolean;
            customMessage?: string;
        } = {}
    ): Promise<string | null> {
        if (!this.canSendNotifications()) {
            return null;
        }

        const notification: Omit<ArrivalNotification, 'id'> = {
            type: 'arrival',
            wellId,
            wellName,
            userId: this.userId!,
            userName: this.userName!,
            actualArrivalTime: new Date(),
            notifyDispatch: options.notifyDispatch ?? this.preferences?.notifyOnArrival ?? true,
            notifyVia: ['app'],
            status: 'pending',
            message: options.customMessage || `Arrived at ${wellName}`,
            createdAt: new Date(),
            companyId: this.companyId!,
        };

        try {
            const id = await this.saveNotification(notification);

            if (notification.notifyDispatch) {
                toast.success('Dispatch notified of your arrival');
            }

            return id;
        } catch (error) {
            console.error('Error sending arrival notification:', error);
            toast.error('Failed to notify dispatch');
            return null;
        }
    }

    /**
     * Send departure notification
     */
    async sendDepartureNotification(
        wellId: string,
        wellName: string,
        options: {
            notifyDispatch?: boolean;
            customMessage?: string;
        } = {}
    ): Promise<string | null> {
        if (!this.canSendNotifications()) {
            return null;
        }

        const notification: Omit<ArrivalNotification, 'id'> = {
            type: 'departure',
            wellId,
            wellName,
            userId: this.userId!,
            userName: this.userName!,
            notifyDispatch: options.notifyDispatch ?? this.preferences?.notifyOnDeparture ?? false,
            notifyVia: ['app'],
            status: 'pending',
            message: options.customMessage || `Departed from ${wellName}`,
            createdAt: new Date(),
            companyId: this.companyId!,
        };

        try {
            const id = await this.saveNotification(notification);
            return id;
        } catch (error) {
            console.error('Error sending departure notification:', error);
            return null;
        }
    }

    /**
     * Send delay notification
     */
    async sendDelayNotification(
        wellId: string,
        wellName: string,
        originalEtaMinutes: number,
        newEtaMinutes: number,
        reason?: string
    ): Promise<string | null> {
        if (!this.canSendNotifications()) {
            return null;
        }

        const delayMinutes = newEtaMinutes - originalEtaMinutes;

        // Only notify if delay exceeds threshold
        if (this.preferences && delayMinutes < this.preferences.delayThresholdMinutes) {
            return null;
        }

        const notification: Omit<ArrivalNotification, 'id'> = {
            type: 'delay',
            wellId,
            wellName,
            userId: this.userId!,
            userName: this.userName!,
            etaMinutes: newEtaMinutes,
            etaTime: new Date(Date.now() + newEtaMinutes * 60 * 1000),
            notifyDispatch: this.preferences?.notifyOnDelay ?? true,
            notifyVia: ['app'],
            status: 'pending',
            message: reason || `Delayed ${delayMinutes} min - new ETA ${newEtaMinutes} min to ${wellName}`,
            createdAt: new Date(),
            companyId: this.companyId!,
        };

        try {
            const id = await this.saveNotification(notification);

            toast.info(`Dispatch notified of ${delayMinutes} min delay`);

            return id;
        } catch (error) {
            console.error('Error sending delay notification:', error);
            return null;
        }
    }

    /**
     * Share ETA with dispatch (manual trigger)
     */
    async shareETAWithDispatch(
        wellName: string,
        etaMinutes: number
    ): Promise<boolean> {
        const id = await this.sendETAUpdate(
            '', // Well ID not required for manual share
            wellName,
            etaMinutes,
            {
                notifyDispatch: true,
                customMessage: `${this.userName} will arrive at ${wellName} in approximately ${etaMinutes} minutes`,
            }
        );

        if (id) {
            toast.success('ETA shared with dispatch', {
                description: `Arriving in ${etaMinutes} min`
            });
            return true;
        }
        return false;
    }

    /**
     * Save notification to Firestore
     */
    private async saveNotification(
        notification: Omit<ArrivalNotification, 'id'>
    ): Promise<string> {
        const notificationsRef = collection(
            db,
            'companies',
            this.companyId!,
            'arrival_notifications'
        );

        const docRef = await addDoc(notificationsRef, {
            ...notification,
            createdAt: Timestamp.now(),
            etaTime: notification.etaTime ? Timestamp.fromDate(notification.etaTime) : null,
            actualArrivalTime: notification.actualArrivalTime
                ? Timestamp.fromDate(notification.actualArrivalTime)
                : null,
        });

        return docRef.id;
    }

    /**
     * Check if service can send notifications
     */
    private canSendNotifications(): boolean {
        return !!(this.companyId && this.userId && this.userName);
    }

    /**
     * Update preferences
     */
    updatePreferences(updates: Partial<ETASharingPreferences>): void {
        if (this.preferences) {
            this.preferences = { ...this.preferences, ...updates };
        }
    }
}

// Singleton export
export const arrivalNotificationService = new ArrivalNotificationService();
